import * as React from 'react';
import classNames from 'classnames';
import { assign as _assign, difference as _difference, includes as _includes } from 'lodash';
import { connect } from 'react-redux';
import { GlobalPrivateState } from 'src/api';
import { ACCESSORIES_TYPE, ConfigurationModel, DrainAccessoriesModel, DrainAccessoriesDefaultModel, ConfigurationDefaultModel } from 'franke-trough-dependencies';
import { requestAPIActions } from 'loopback-react-request';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './DrainAccessories.messages';
import { isConfigurationEditable } from 'src/redux/utils.redux';

export interface DrainAccessoriesProps {
  configuration: GlobalPrivateState['configuration'];
  dispatch: any;
}

export interface DrainAccessoriesState {
  drain: ACCESSORIES_TYPE;
  siphon: boolean;
}

const DrainLabel: React.SFC<{value: ACCESSORIES_TYPE}> = (props) => {
  switch (props.value) {
    case 'without':
      return (
        <span>
          <FormattedMessage {...MESSAGES.drainLabelWithoutDrain} />
        </span>
      );
    case 'hood-chrome':
      return (
        <span>
          <FormattedMessage {...MESSAGES.drainLabelHoodChromeDrain} />
        </span>
      );
    case 'hood':
      return (
        <span>
          <FormattedMessage {...MESSAGES.drainLabelHoodDrain} />
        </span>
      );
    case 'sieve':
      return (
        <span>
          <FormattedMessage {...MESSAGES.drainLabelSieveDrain} />
        </span>
      );
    case 'eccentric':
      return (
        <span>
          <FormattedMessage {...MESSAGES.drainLabelEccentric} />
        </span>
      );
    default:
      return null;
  }  
};

export class DrainAccessoriesPartial extends React.Component<DrainAccessoriesProps, DrainAccessoriesState> {
  constructor(props: DrainAccessoriesProps) {
    super(props);
    this.state = {
      drain: 'without',
      siphon: false,
    };

    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handleRadioChange = this.handleRadioChange.bind(this);
  }

  componentDidMount() {
    this.setState({
      drain: this.drain,
      siphon: this.siphon,
    });
  }

  componentDidUpdate(prevProps: DrainAccessoriesProps) {
    const configuration = this.configuration;
    const prevConfiguration = prevProps.configuration && prevProps.configuration.result;
    if ((configuration && configuration.updated_at) !== (prevConfiguration && prevConfiguration.updated_at)) {
      if (this.drain !== this.state.drain) {
        this.setState({
          drain: this.drain,
        });
      }
      if (this.siphon !== this.state.siphon) {
        this.setState({
          siphon: this.siphon,
        });
      }
    }
  }

  public render() {
    const {
      drain,
      siphon,
    } = this.state;
    
    return (
      <div className="partial-drain-accessories position-relative pb-4">
        {this.drainDefaults && this.drainDefaults.allValues && this.drainDefaults.allValues.map((value, index) => {
          const key = `drain_values_${index}`;
          const selected = drain || this.drain;
          const disabled = !(_includes(this.drainDefaults && this.drainDefaults.values, value));

          return (
            <div key={key} className="col-12">
              <label className={classNames({disabled: this.disabled || disabled})}>
                <DrainLabel value={value} />
                <input
                  checked={selected === value}
                  disabled={this.disabled || disabled}
                  name="drain" 
                  onChange={this.handleRadioChange}
                  type="radio"
                  value={value}
                />
                <span className="checkmark" />
              </label>
            </div>
          );
        })}
        <div className="col-12">
          <label className={classNames({disabled: this.disabled || this.siphonIsDisabled})}>
            <FormattedMessage {...MESSAGES.drainLabelSiphon} />
            <input 
              className={classNames({disabled: this.disabled || this.siphonIsDisabled})}
              checked={siphon}
              disabled={this.disabled || this.siphonIsDisabled}
              name="siphon"
              onChange={this.handleCheckboxChange}
              type="checkbox"
              value={(siphon || this.siphon).toString()}
            />
            <span className="checkmark" />
          </label>
        </div>
      </div>
    );
  }

  private get configuration(): ConfigurationModel | undefined {
    return this.props.configuration && this.props.configuration.result;
  }

  private get drainAccessoriesDefaults(): ConfigurationDefaultModel['drainAccessories'] | undefined {
    return this.configuration && 
      this.configuration.defaults &&
      this.configuration.defaults.drainAccessories;
  }

  private dispatchConfiguration(props: DrainAccessoriesState) {
    if (this.disabled) { return; }

    const drainAccessories: DrainAccessoriesModel | null = (this.configuration as ConfigurationModel).drainAccessories;
    const data: DrainAccessoriesModel = _assign({}, drainAccessories, {
      drain: props.drain,
      siphon: props.siphon
    });

    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {drainAccessories: data}
        },
      },
      true,
    ));
  }

  private get drain(): DrainAccessoriesModel['drain'] {
    return this.configuration && 
      this.configuration.drainAccessories &&
      this.configuration.drainAccessories.drain ? this.configuration.drainAccessories.drain : 'without';
  }
    
  private get disabled(): boolean {
    return !isConfigurationEditable() || !this.configuration;
  }
  
  private get drainDefaults(): DrainAccessoriesDefaultModel['drain'] | undefined {
    return this.drainAccessoriesDefaults && this.drainAccessoriesDefaults.drain;
  }

  private handleRadioChange(event: React.SyntheticEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement;
    const drain = target.value as ACCESSORIES_TYPE;
    const state: DrainAccessoriesState = {
      drain,
      siphon: this.state.siphon,
    };
    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private handleCheckboxChange(event: React.SyntheticEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement;
    const siphon = target.checked;
    const state: DrainAccessoriesState = {
      drain: this.state.drain,
      siphon,
    };
    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private get siphon(): DrainAccessoriesModel['siphon'] {
    return this.configuration && 
      this.configuration.drainAccessories &&
      this.configuration.drainAccessories.siphon ? this.configuration.drainAccessories.siphon : false;
  }

  private get siphonDefaults(): DrainAccessoriesDefaultModel['siphon'] | undefined {
    return this.drainAccessoriesDefaults && this.drainAccessoriesDefaults.siphon;
  }

  private get siphonIsDisabled(): boolean {
    if (this.siphonDefaults && this.siphonDefaults.values.length > 1) {
      return false;
    }

    return true;
  }
}

export const DrainAccessories = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration,
}))(DrainAccessoriesPartial);

export default DrainAccessories;